import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { INSURANCE_TYPE_PASSPORT_CARD, PRODUCT_ID_PASSPORT_CARD } from 'constants/insurance'

export const EMPTY_PASSPORT_CARD_TRAVELLER_ADDRESS: App.PassportCardTravellerAddress = {
  country: 'AU',
  state: '',
  street: '',
  city: '',
  postcode: '',
}

export const PASSPORT_CARD_MAX_START_DATE_IN_DAYS = 365
export const PASSPORT_CARD_UTM_CAMPAIGN = 'passport_card'

export function isPassportCardInsuranceItem(item: App.Checkout.InsuranceItem): boolean {
  return (
    item.insuranceType === INSURANCE_TYPE_PASSPORT_CARD ||
    item.productId === PRODUCT_ID_PASSPORT_CARD ||
    item.passportCardProposalId !== undefined ||
    !!item.passportCardOptionalBenefitIds?.length ||
    !!item.passportCardTravellers?.length
  )
}

export function findPassportCardInsuranceItem(
  items: Array<App.Checkout.InsuranceItem>,
): App.Checkout.InsuranceItem | undefined {
  return items.find(isPassportCardInsuranceItem)
}

export function isPassportCardStartDateWithinAllowedRange(
  startDate?: string,
  referenceDate = moment(),
): boolean {
  if (!startDate) return false

  const currentDay = moment(referenceDate).startOf('day')
  const tripStartDay = moment(startDate).startOf('day')
  const daysUntilTripStart = tripStartDay.diff(currentDay, 'days')

  return daysUntilTripStart >= 0 && daysUntilTripStart <= PASSPORT_CARD_MAX_START_DATE_IN_DAYS
}

export function getPassportCardMaxStartDate(referenceDate = moment()): string {
  return moment(referenceDate)
    .startOf('day')
    .add(PASSPORT_CARD_MAX_START_DATE_IN_DAYS, 'days')
    .format(ISO_DATE_FORMAT)
}

export function travellersFromOccupants(occupants: App.Occupants): Array<App.PassportCardTravellerDetail> {
  const adults = Array.from({ length: occupants.adults || 0 }, () => ({ type: 'adult' as const, dateOfBirth: '' }))
  const childrenCount = (occupants.children || 0) + (occupants.infants || 0)
  const children = Array.from({ length: childrenCount }, () => ({ type: 'child' as const, dateOfBirth: '' }))
  return [...adults, ...children]
}

export function occupantsFromTravellers(travellers: Array<App.PassportCardTravellerDetail>): App.Occupants {
  return travellers.reduce<App.Occupants>((result, traveller) => {
    if (traveller.type === 'adult') {
      return {
        ...result,
        adults: (result.adults || 0) + 1,
      }
    }
    return {
      ...result,
      children: (result.children || 0) + 1,
    }
  }, { adults: 0, children: 0, infants: 0 })
}

export function travellerTypeFromDob(dateOfBirth: string): App.PassportCardTravellerType {
  const age = moment().diff(moment(dateOfBirth, 'YYYY-MM-DD', true), 'years')
  return age >= 18 ? 'adult' : 'child'
}

export function travellersFromTravellerDetails(
  travellersDetails: Array<App.PassportCardTravellerDetail>,
): Array<App.PassportCardInsuranceTraveller> {
  return travellersDetails.map(traveller => ({
    firstName: '',
    lastName: '',
    ageRange: traveller.type,
    dateOfBirth: traveller.dateOfBirth || '',
    address: { ...EMPTY_PASSPORT_CARD_TRAVELLER_ADDRESS },
  }))
}

export function travellerDetailsFromInsuranceTravellers(
  travellers: Array<App.PassportCardInsuranceTraveller>,
): Array<App.PassportCardTravellerDetail> {
  return travellers.map(traveller => ({
    type: traveller.ageRange,
    dateOfBirth: traveller.dateOfBirth || '',
  }))
}

export function mergeTravellerDetailsIntoInsuranceTravellers(
  travellersDetails: Array<App.PassportCardTravellerDetail>,
  existingTravellers: Array<App.PassportCardInsuranceTraveller> = [],
): Array<App.PassportCardInsuranceTraveller> {
  return travellersDetails.map((travellerDetail, index) => {
    const existingTraveller = existingTravellers[index]

    return {
      firstName: existingTraveller?.firstName || '',
      lastName: existingTraveller?.lastName || '',
      ageRange: travellerDetail.type,
      dateOfBirth: travellerDetail.dateOfBirth || '',
      address: existingTraveller?.address || { ...EMPTY_PASSPORT_CARD_TRAVELLER_ADDRESS },
      email: existingTraveller?.email,
      phonePrefix: existingTraveller?.phonePrefix,
      phone: existingTraveller?.phone,
    }
  })
}
